import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pill } from '@/features/feeds/components/FeedsNav'
import type { ProductRow } from '../api/growthApi'
import { ErrorNote, GrowthFrame, Segmented, money, num, pct, useSelection } from '../components/GrowthFrame'
import { useProducts } from '../hooks/useGrowth'

const SORTS = [
  { value: 'cost', label: 'Spend' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'impressions', label: 'Impr.' },
  { value: 'purchases', label: 'Purchases' },
]

const TIER_TONE = { hero: 'green', core: 'blue', tail: 'slate' } as const

export function ProductCell({ p, compact }: { p: ProductRow; compact?: boolean }) {
  const size = compact ? 'h-9 w-9' : 'h-12 w-12'
  return (
    <div className="flex items-center gap-3">
      {p.image ? <img src={p.image} alt="" loading="lazy" className={`${size} shrink-0 rounded object-cover`} /> : <div className={`${size} shrink-0 rounded bg-muted`} />}
      <div className="min-w-0">
        <div className={`truncate font-medium ${compact ? 'max-w-56 text-xs' : 'max-w-md text-sm'}`} title={p.title || p.item_id}>
          {p.link ? <a href={p.link} target="_blank" rel="noreferrer" className="hover:underline">{p.title || p.item_id}</a> : p.title || p.item_id}
        </div>
        <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
          <span>{p.kind === 'set' ? 'Set' : 'Print'}</span>
          {p.room && <span>· {p.room}</span>}
          {p.tier && <Pill tone={TIER_TONE[p.tier]}>{p.tier}</Pill>}
          {!p.inFeed && <Pill tone="amber" title="No longer in this market's feed">not in feed</Pill>}
          {p.inFeed && p.eligible === false && <Pill tone="amber">excluded</Pill>}
        </div>
      </div>
    </div>
  )
}

export function ProductsPage() {
  const sel = useSelection()
  const [text, setText] = useState(sel.params.get('q') || '')
  const campaign = sel.params.get('campaign') || undefined
  const sort = sel.params.get('sort') || 'cost'
  useEffect(() => {
    const t = setTimeout(() => sel.set({ q: text || null }), 300)
    return () => clearTimeout(t)
  }, [text]) // eslint-disable-line react-hooks/exhaustive-deps
  const q = useProducts(sel.account?.customerId, { days: sel.days, campaign, q: sel.params.get('q') || undefined, sort, limit: 200 })
  const cur = sel.account?.currency || 'USD'

  return (
    <GrowthFrame
      title="Products"
      description="Which feed items Shopping showed and what they earned, from the nightly sync (runs to yesterday). Tier is the market's sales tier (custom_label_3) the campaigns bid on."
    >
      {q.error && <ErrorNote error={q.error} />}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Filter by item id (e.g. art-7652, set-quiet)…" className="h-8 w-72" />
        <Segmented label="Sort" value={sort} options={SORTS} onChange={(v) => sel.set({ sort: v })} />
        {campaign && <Pill tone="blue">one campaign <button className="ml-1" onClick={() => sel.set({ campaign: null })} aria-label="Clear campaign">×</button></Pill>}
        {q.data && <span className="ml-auto text-xs text-muted-foreground">{num(q.data.totals.items)} products got {num(q.data.totals.impressions)} impressions in the window</span>}
      </div>
      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {!q.data ? <Skeleton className="h-64" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Impr.</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                  <TableHead className="text-right">CPC</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Purchases</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.data.map((p) => (
                  <TableRow key={p.item_id}>
                    <TableCell><ProductCell p={p} /></TableCell>
                    <TableCell className="text-right tabular-nums">{num(p.impressions)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(p.clicks)}</TableCell>
                    <TableCell className="text-right tabular-nums">{pct(p.impressions ? p.clicks / p.impressions : null, 2)}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.cpc != null ? money(p.cpc, cur) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(p.cost, cur)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(p.purchases)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(p.value, cur)}</TableCell>
                  </TableRow>
                ))}
                {!q.data.data.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground">No products served in this window.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </GrowthFrame>
  )
}
